<template>
  <LoadingSpinner :loading='loading'> </LoadingSpinner>

  <div class="container mt-4 mb-4">
    <div style="max-width:560px">
        <h3>Merchant</h3>
        <div v-if='updated'>
          <span class='message-info'>Merchant innstillingene er oppdatert</span>
        </div>

        <div class="mt-4">
          <span class="bo_label">Logo</span>
          <input type="file" class="form-control" accept="image/*" ref="logoFile" @change="selectLogoFile" />
          <button type='button' class='bo_btn mt-2' @click='uploadLogoFile' :disabled="!activateUploadLogoFileButton"> Last opp </button>
        </div>

        <div class="mt-4">
          <span class="ingredient-title">Hvilken funksjonalitet av løsningen bruker denne merchant?</span>
          <select class="form-select" v-model="merchantSetting.useFunctionality">
            <option value="">Velg funksjonalitet</option>
            <option value="WEBSITE">Website</option>
            <option value="TAKEAWAY">Takeaway</option>
            <option value="KIOSK_TAKEAWAY">Kiosk & Takeaway</option>
            <option value="POS">POS</option>
            <option value="ALL">All</option>
          </select>
        </div>

        <div>
          <span class="ingredient-title">Skal denne merchanten bruke SaaS-funksjonene (Website Builder, Aktiver VIVA-betaling, Abonnement og betaling)?</span>
          <label class="ms-3">
            <input type="radio" v-model="merchantSetting.useSaaSFeatures" v-bind:value="true"/> Ja
          </label>
          <br/>
          <label class="mt-2 ms-3">
            <input type="radio" v-model="merchantSetting.useSaaSFeatures" v-bind:value="false"/> Nei
          </label>
          <div style="color:#888; font-size:0.8rem" class="ms-3 mt-1">
            Nye merchants får Ja automatisk ved onboarding. Eksisterende/legacy merchants står på Nei —
            da skjules SaaS-menypunktene og trial-/oppsettsbannerne på Oversikt. Brukeren må logge inn på
            nytt (eller laste innstillingene på nytt) for at endringen skal synes i menyen.
          </div>
        </div>

        <div v-if="isSuperAdmin">
          <span class="ingredient-title">Skal denne merchanten kreve SMS-tofaktor (2FA) ved innlogging?</span>
          <label class="ms-3">
            <input type="radio" v-model="sms2fa.requireSms2fa" v-bind:value="true"/> Ja
          </label>
          <br/>
          <label class="mt-2 ms-3">
            <input type="radio" v-model="sms2fa.requireSms2fa" v-bind:value="false"/> Nei
          </label>
          <div style="color:#888; font-size:0.8rem" class="ms-3 mt-1">
            Gjelder <b>alle brukere</b> hos denne merchanten (ADMIN, EMPLOYEE, ACCOUNTANT). Når det er på,
            godtar API-et heller ikke lenger passord (HTTP Basic) for personer — kun sesjonstoken utstedt
            etter SMS-koden. Terminaler er ikke berørt: de bruker sin egen enhetsspesifikke nøkkel.
            <span v-if="sms2fa.mode === 'OFF'"><br/><b>NB:</b> global bryter står på OFF — 2FA er slått av
            for alle uansett hva som velges her.</span>
            <span v-else-if="sms2fa.mode === 'ALL'"><br/><b>NB:</b> global bryter står på ALL — 2FA er
            påkrevd for alle merchants uansett hva som velges her.</span>
          </div>

          <div class="ms-3 mt-2" style="font-size:0.85rem" v-if="!sms2fa.loading && sms2fa.terminals.readable">
            <span v-if="sms2fa.terminals.total === 0" style="color:#888">
              Ingen terminaler registrert.
            </span>
            <span v-else-if="sms2fa.terminals.withoutDeviceCredential.length === 0" style="color:#1a7f37">
              ✓ Alle {{ sms2fa.terminals.total }} terminaler har egen enhetsnøkkel — trygt å slå på.
            </span>
            <span v-else style="color:#8A6800">
              ⚠ {{ sms2fa.terminals.withoutDeviceCredential.length }} av {{ sms2fa.terminals.total }}
              terminaler har <b>ikke</b> egen enhetsnøkkel ennå og vil få 401 når 2FA slås på. De må
              oppgraderes og logge inn én gang først.
              <ul class="mt-1 mb-0">
                <li v-for="t in sms2fa.terminals.withoutDeviceCredential" :key="t.deviceId || t.terminalId">
                  Terminal {{ t.terminalId }}
                  <span v-if="t.terminalType">({{ t.terminalType }})</span>
                  <span v-if="t.deviceName"> – {{ t.deviceName }}</span>
                  <span v-if="!t.deviceId" style="color:#888"> – ingen deviceId registrert</span>
                </li>
              </ul>
            </span>
          </div>

          <div class="ms-3 mt-2">
            <button class="btn btn-secondary btn-sm" :disabled="sms2fa.saving" @click="saveSms2fa()">
              {{ sms2fa.saving ? 'Lagrer…' : 'Lagre 2FA-innstilling' }}
            </button>
            <span v-if="sms2fa.saved" class="ms-2" style="color:#1a7f37; font-size:0.85rem">Lagret</span>
            <span v-if="sms2fa.error" class="ms-2" style="color:#b00020; font-size:0.85rem">{{ sms2fa.error }}</span>
          </div>
        </div>

        <!-- CAGISTA's ISV fee for THIS merchant (SUPER_ADMIN). Its own endpoint and a server-only document — never
             merchant_setting, which the merchant's own ADMIN can save. Blank = the platform rate in VIVA Partner
             Config. CagistaServices VivaIsvFeeOverrides, docs/viva-fees.md. -->
        <div v-if="isSuperAdmin" class="mt-3">
          <span class="ingredient-title">CAGISTAs ISV-gebyr for denne merchanten</span>
          <div style="color:#888; font-size:0.8rem" class="ms-3 mt-1">
            Overstyrer satsene i <a href="/vivaPartnerConfig">VIVA Partner Config</a> for denne merchanten. Tomt felt
            = plattformsatsen. Gebyret trekkes fra merchantens utbetaling fra VIVA — gjesten betaler det samme. Gjelder
            fra neste nettbestilling; kassene og kioskene henter satsen ved neste VIVA-betaling.
          </div>
          <div v-if="isvFee.loading" class="ms-3 mt-2" style="color:#888; font-size:0.85rem">Henter…</div>
          <div v-else class="row g-2 ms-1 mt-1">
            <div v-for="field in isvFeeFields" :key="field.key" class="col-sm-6">
              <label class="mf-isv-label">{{ field.label }}</label>
              <div style="position:relative">
                <input class="form-control" type="text" inputmode="decimal" style="padding-right:34px"
                       v-model="isvFee[field.key]" :placeholder="'Tomt = plattform (' + rateText(isvFee[field.platform]) + ')'" />
                <span class="mf-isv-suffix">%</span>
              </div>
              <span class="mf-isv-hint" :class="{ 'mf-isv-hint--bad': !isvFeeCheck(isvFee[field.key]).ok }">
                {{ isvFeeApplies(field) }}</span>
            </div>
          </div>
          <div class="ms-3 mt-2 d-flex align-items-center flex-wrap" style="gap:8px">
            <button type="button" class="bo_btn_secondary" :disabled="!isvFeeCanSave" @click="saveIsvFee(isvFee.web, isvFee.terminal)">
              {{ isvFee.saving ? 'Lagrer…' : 'Lagre ISV-gebyr' }}
            </button>
            <button v-if="isvFee.hasOwn" type="button" class="bo_btn_secondary" :disabled="isvFee.saving"
                    @click="saveIsvFee('', '')">Bruk plattformsatsene</button>
            <span v-if="isvFee.saved" style="color:#1a7f37; font-size:0.85rem">Lagret</span>
            <span v-if="isvFee.error" style="color:#b00020; font-size:0.85rem">{{ isvFee.error }}</span>
          </div>
          <div v-if="isvFee.updatedBy" class="ms-3 mt-1" style="color:#888; font-size:0.78rem">
            Sist endret {{ formatIsvTime(isvFee.updatedTime) }} av {{ isvFee.updatedBy }}.
          </div>
        </div>

        <div>
          <span class="ingredient-title">Skal SPISE HER meny vises på nettsiden?</span>
          <label class="ms-3">
            <input type="radio" v-model="merchantSetting.websiteShowDineInMenu" v-bind:value="true"/> Ja
          </label>
          <br/>
          <label class="mt-2 ms-3">
            <input type="radio" v-model="merchantSetting.websiteShowDineInMenu" v-bind:value="false"/> Nei
          </label>
        </div>

        <div>
          <span class="ingredient-title">Skal "Tilgjengelig på følgende dager" vises for oppretting og redigering av produkter?</span>
          <label class="ms-3">
            <input type="radio" v-model="merchantSetting.showAvailableOnDays" v-bind:value="true"/> Ja
          </label>
          <br/>
          <label class="mt-2 ms-3">
            <input type="radio" v-model="merchantSetting.showAvailableOnDays" v-bind:value="false"/> Nei
          </label>
        </div>

        <div>
          <span class="ingredient-title">Skal "Angi de mulige valgene for sterkhetsgrad" vises for oppretting og redigering av produkter?</span>
          <label class="ms-3">
            <input type="radio" v-model="merchantSetting.showSpicyLevelSelections" v-bind:value="true"/> Ja
          </label>
          <br/>
          <label class="mt-2 ms-3">
            <input type="radio" v-model="merchantSetting.showSpicyLevelSelections" v-bind:value="false"/> Nei
          </label>
        </div>

        <div>
          <span class="ingredient-title">Skal systemet sende bekreftelsesmelding på SMS automatisk?</span>
          <label class="ms-3">
            <input type="radio" v-model="merchantSetting.takeawayAutoSmsConfirm" v-bind:value="true"/> Ja
          </label>
          <br/>
          <label class="mt-2 ms-3">
            <input type="radio" v-model="merchantSetting.takeawayAutoSmsConfirm" v-bind:value="false"/> Nei
          </label>
          <!-- Names BOTH messages the switch governs. Until 2026-09-21 it governed only the first, and a
               merchant who chose «Nei» kept seeing their guests texted the second — which reads as a
               confirmation too. A switch that lists what it switches cannot promise more than it keeps. -->
          <div class="form-text ms-3">
            Gjelder både ordrebekreftelsen for nettbestillinger og meldingen med bestillingsnummer som kasse og
            kiosk sender etter betaling. Hver SMS koster kr 1.
          </div>
        </div>

        <div>
          <span class="ingredient-title">Skal systemet ta dagens oppgjør for nettbutikken automatisk?</span>
          <select class="form-select" v-model="merchantSetting.autoSettleAtTime">
            <option value="">Nei</option>
            <option value="23:55">Ja, kl. 23:55 hver dag</option>
            <option value="05:00">Ja, kl. 05:00 hver dag</option>
          </select>
        </div>

        <div>
          <span class="ingredient-title">Skal systemet merke ordrer som fullførte ved dagens slutt?</span>
          <label class="ms-3">
            <input type="radio" v-model="merchantSetting.autoSetOrdersAsCompleted" v-bind:value="true"/> Ja
          </label>
          <br/>
          <label class="mt-2 ms-3">
            <input type="radio" v-model="merchantSetting.autoSetOrdersAsCompleted" v-bind:value="false"/> Nei
          </label>
          <!-- Bestilt as well as Bekreftet since 2026-10-17 (CagistaServices SetOrdersAsCompleted), and once a day at
               the settle time (AutoSettle.isEndOfDay). The label used to say «bekreftede ordrer» only. -->
          <div class="form-text ms-3">
            Gjelder både bestilte og bekreftede ordrer der hente- eller leveringstiden har passert, og ordrer uten
            oppgitt tid. Skjer ved dagens oppgjør, eller kl. 23:55 hvis oppgjøret ikke tas automatisk.
          </div>
        </div>

        <div class='bo_hr mt-4'>
          <hr/>
        </div>

        <div class='mb-5'>
          <button v-if='activateSaveButton' type='button' class='bo_btn' @click='save'> Lagre </button>
          <button v-else type='button' class='bo_btn' @click='save' disabled='true'> Lagre </button>
        </div>
    </div>
  </div>
</template>

<script>
  import SettingsService from '@/services/settings/SettingsService';
  import MerchantUtil from "@/util/MerchantUtil";
  import UserUtil from "@/util/UserUtil";
  import LoadingSpinner from "@/components/other/LoadingSpinner";
  import MediaFileService from "@/services/media/MediaFileService";

  export default {
    components: {LoadingSpinner},
    data() {
      return {
        loading: true,

        updated: false,
        merchantSetting: {
          type: "merchant_setting",
          merchant: '',
          logoImageFile: '',
          useFunctionality: '',
          useSaaSFeatures: false,
          websiteShowDineInMenu: false,
          showAvailableOnDays: false,
          showSpicyLevelSelections: false,
          takeawayAutoSmsConfirm: false,
          autoSettleAtTime: '',
          autoSetOrdersAsCompleted: false
        },
        // Saved through its own SUPER_ADMIN endpoint, not with the rest of merchant_setting — see
        // SettingsService.saveSms2faSetting.
        sms2fa: {
          loading: true,
          saving: false,
          saved: false,
          error: '',
          mode: 'PER_MERCHANT',
          requireSms2fa: false,
          terminals: { readable: false, total: 0, withDeviceCredential: 0, withoutDeviceCredential: [] }
        },
        // CAGISTA's ISV fee for this merchant — its own SUPER_ADMIN endpoint, see SettingsService.saveVivaIsvFee.
        // web/terminal as typed (blank = platform); the platform rates come with the answer.
        isvFee: {
          loading: true, saving: false, saved: false, error: '',
          web: '', terminal: '', platformWeb: '', platformTerminal: '', hasOwn: false, updatedBy: '', updatedTime: ''
        }
      };
    },
    mounted() {
        this.getMerchantSetting();
        if(this.isSuperAdmin){
          this.getSms2faStatus();
          this.getIsvFee();
        }
    },
    computed: {
      isSuperAdmin(){
        return UserUtil.isSuperAdmin();
      },
      isvFeeFields(){
        return [
          { key: 'web', platform: 'platformWeb', label: 'Nettbestilling' },
          { key: 'terminal', platform: 'platformTerminal', label: 'Kasse og kiosk' }
        ];
      },
      isvFeeCanSave(){
        return !this.isvFee.loading && !this.isvFee.saving
            && this.isvFeeCheck(this.isvFee.web).ok && this.isvFeeCheck(this.isvFee.terminal).ok;
      },
      activateUploadLogoFileButton(){
        if(this.merchantSetting.logoImageFile !== ''){
          return true;
        } else {
          return false;
        }
      },
      activateSaveButton(){
        if(this.merchantSetting.useFunctionality.length > 0){
          return true;
        } else {
          return false;
        }
      }
    },
    methods: {
      getMerchantSetting(){
        SettingsService.getMerchantSetting().then(
            (response) => {
              if(response.data !== null){
                this.merchantSetting = response.data;
                // Legacy docs lack the field — show "Nei" (matches the effective behavior: hidden).
                if(this.merchantSetting.useSaaSFeatures === undefined){
                  this.merchantSetting.useSaaSFeatures = false;
                }
                console.log(JSON.stringify(this.merchantSetting));
              }

              this.loading = false;
            },
            (error) => {
              console.log(error);
            }
        );
      },
      getSms2faStatus(){
        SettingsService.getSms2faStatus().then(
            (response) => {
              const data = response.data;
              this.sms2fa.mode = data.mode;
              this.sms2fa.requireSms2fa = data.requireSms2fa;
              this.sms2fa.terminals = data.terminals;
              this.sms2fa.loading = false;
            },
            (error) => {
              console.log(error);
              // Do NOT fail quietly: the panel would render its defaults and look loaded, so the first
              // sign of trouble was the save failing later. Say it now, with the status — a 403 here
              // means the session is not the SUPER_ADMIN it appears to be, which is worth knowing
              // before touching a security setting.
              this.sms2fa.error = this.sms2faError(error, 'Kunne ikke hente 2FA-status.');
              this.sms2fa.loading = false;
            }
        );
      },
      /** A message that says what actually went wrong, rather than only that something did. */
      sms2faError(error, fallback){
        const status = error && error.response ? error.response.status : 0;
        if(status === 403){
          return 'Ingen tilgang (403) — innlogget bruker mangler SUPER_ADMIN-rettighet på serveren.';
        }
        if(status === 401){
          return 'Sesjonen er utløpt (401) — logg inn på nytt.';
        }
        return status ? fallback + ' (HTTP ' + status + ')' : fallback;
      },
      saveSms2fa(){
        this.sms2fa.saving = true;
        this.sms2fa.saved = false;
        this.sms2fa.error = '';
        SettingsService.saveSms2faSetting(this.sms2fa.requireSms2fa).then(
            () => {
              // Keep the page's own copy in step, so a later "Lagre" of the rest of merchant_setting
              // does not post a stale value back over it.
              this.merchantSetting.requireSms2fa = this.sms2fa.requireSms2fa;
              this.sms2fa.saved = true;
              this.sms2fa.saving = false;
            },
            (error) => {
              console.log(error);
              this.sms2fa.error = this.sms2faError(error, 'Kunne ikke lagre.');
              this.sms2fa.saving = false;
            }
        );
      },
      getIsvFee(){
        SettingsService.getVivaIsvFee().then(
            (response) => {
              this.applyIsvFee(response.data);
              this.isvFee.loading = false;
            },
            (error) => {
              console.log(error);
              this.isvFee.error = this.sms2faError(error, 'Kunne ikke hente ISV-gebyret.');
              this.isvFee.loading = false;
            }
        );
      },
      applyIsvFee(data){
        this.isvFee.web = this.toComma(data.web);
        this.isvFee.terminal = this.toComma(data.terminal);
        this.isvFee.platformWeb = data.platformWeb;
        this.isvFee.platformTerminal = data.platformTerminal;
        this.isvFee.hasOwn = !!(data.web || data.terminal);
        this.isvFee.updatedBy = data.updatedBy || '';
        this.isvFee.updatedTime = data.updatedTime || '';
      },
      /** Blank fields = this merchant follows the platform rates again. */
      saveIsvFee(web, terminal){
        this.isvFee.saving = true;
        this.isvFee.saved = false;
        this.isvFee.error = '';
        SettingsService.saveVivaIsvFee(web, terminal).then(
            (response) => {
              this.applyIsvFee(response.data);
              this.isvFee.saved = true;
              this.isvFee.saving = false;
            },
            (error) => {
              console.log(error);
              const data = error && error.response ? error.response.data : null;
              this.isvFee.error = data && data.error ? data.error : this.sms2faError(error, 'Kunne ikke lagre.');
              this.isvFee.saving = false;
            }
        );
      },
      /**
       * Mirrors the server's rule (CagistaServices VivaIsvFee.parsePercent) only to show a slip before saving: blank, or a
       * percentage from 0 to 1 with at most three decimals. The server is the check.
       */
      isvFeeCheck(text){
        const t = String(text === undefined || text === null ? '' : text).replace('%', '').replace(',', '.').trim();
        if(!t){
          return { ok: true, blank: true };
        }
        if(!/^\d+(\.\d+)?$/.test(t) || (t.split('.')[1] || '').replace(/0+$/, '').length > 3){
          return { ok: false, text: 'Skriv et tall i prosent, f.eks. 0,10 — høyst tre desimaler.' };
        }
        if(Math.round(Number(t) * 1000) > 1000){
          return { ok: false, text: 'Over 1 % — skriv 0,10 for 0,10 %.' };
        }
        return { ok: true, thousandths: Math.round(Number(t) * 1000) };
      },
      /** What applies to this merchant with the field as typed, before saving. */
      isvFeeApplies(field){
        const check = this.isvFeeCheck(this.isvFee[field.key]);
        if(!check.ok){
          return check.text;
        }
        if(check.blank){
          return 'Gjelder: ' + this.rateText(this.isvFee[field.platform]) + ' (plattformsatsen)';
        }
        return 'Gjelder: ' + this.thousandthsText(check.thousandths) + ' (egen sats for denne merchanten)';
      },
      /** «0,10 %», «0,15 %», «0,00 %» — a rate as the server reports it («0.10»), with two decimals. */
      rateText(rate){
        const n = Number(String(rate === undefined || rate === null ? '' : rate).replace(',', '.'));
        return Number.isFinite(n) && String(rate).trim() !== '' ? this.thousandthsText(Math.round(n * 1000)) : '–';
      },
      /** At least two decimals, a third only when it is used: «0,10 %», «0,125 %». */
      thousandthsText(thousandths){
        const fixed = (thousandths / 1000).toFixed(3);
        return (fixed.endsWith('0') ? fixed.slice(0, -1) : fixed).replace('.', ',') + ' %';
      },
      toComma(value){
        return value === undefined || value === null ? '' : String(value).replace('.', ',');
      },
      formatIsvTime(iso){
        const d = new Date(iso);
        return isNaN(d.getTime()) ? iso : d.toLocaleString('nb-NO', { dateStyle: 'short', timeStyle: 'short' });
      },
      selectLogoFile(){
        this.merchantSetting.logoImageFile = this.$refs.logoFile.files[0];
        console.log("logoImageFile: " + this.merchantSetting.logoImageFile.name)
      },
      uploadLogoFile(){
        this.loading = true;
        MediaFileService.uploadImageFile(this.merchantSetting.logoImageFile).then(
            (response) => {
              let imageUrl = response.data;
              if(imageUrl){
                console.log("logoImageFile: " + imageUrl);
                this.merchantSetting.logoImageFile = imageUrl;
              } else {
                console.log("Uploading logoImageFile failed");
              }
              this.loading = false;
            },
            (error) => {
              console.log(error);
            }
        );
      },
      save(){
        this.loading = true;
        this.merchantSetting.merchant = MerchantUtil.getMerchant();
        this.editMerchantSetting(this.merchantSetting);
      },
      editMerchantSetting(merchantSetting){
        SettingsService.editMerchantSetting(merchantSetting).then(
            (response) => {
              let documentId = response.data;
              this.merchantSetting.id = documentId;
              if(documentId){
                this.updated = true;
              } else {
                console.log("Editing merchantSetting failed");
              }
              this.loading = false;
            },
            (error) => {
              console.log(error);
            }
        );
      }
    }
  };
</script>

<style scoped>
/* CAGISTA's ISV fee block (SUPER_ADMIN). The unit sits inside the field, as on VIVA Partner Config. */
.mf-isv-label { font-size: 0.85rem; color: #888; margin-bottom: 2px; display: block; }
.mf-isv-suffix { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); color: #888; pointer-events: none; }
.mf-isv-hint { display: block; color: #888; font-size: 0.78rem; margin-top: 2px; }
.mf-isv-hint--bad { color: #b00020; }
</style>
